// Mobile Calculator Landing - Calculator Selection Screen
// Clean, touch-friendly interface for selecting which calculator to use
// Displays on mobile (<768px), desktop shows normal interface

import React, { useState, lazy, Suspense } from 'react';
import UpdateMarker from './UpdateMarker.js';
import { SubscriptionManager } from '../SubscriptionManager.js';
import UserTestimonialsSection from './UserTestimonialsSection.js';

const SubscriptionManagement = lazy(() =>
  import('./SubscriptionManagement.js').then((m) => ({
    default: m.SubscriptionManagement || m.default
  }))
);

export const MobileCalculatorLanding = ({ onSelectCalculator, currentCalculator, onShowGuide }) => {
    const [showBilling, setShowBilling] = useState(false);
    const subInfo = SubscriptionManager.getSubscriptionInfo();
    const calculators = [
        {
            id: 'basic',
            name: 'Basic Retirement',
            icon: '📊',
            description: 'Quick retirement snapshot',
            features: ['Fast results', 'Age pension', 'Simple inputs'],
            badge: 'FREE',
            badgeColor: 'bg-green-500'
        },
        {
            id: 'working-accumulation',
            name: 'Accumulation',
            icon: '💰',
            description: 'Grow your super while working',
            features: ['Track growth', 'Contributions', 'Projections'],
            badge: 'FREE',
            badgeColor: 'bg-green-500'
        },
        {
            id: 'advanced',
            name: 'Advanced Stress Test',
            icon: '🎯',
            description: 'Australian retirement calculator for bad timing, market crashes, and Monte Carlo survival analysis',
            features: ['Stress-test crashes', 'Phased retirement', 'Survival %'],
            badge: 'TRY',
            badgeColor: 'bg-emerald-600'
        },
        {
            id: 'smsf',
            name: 'SMSF Suite',
            icon: '🏢',
            description: 'SMSF planning tools and calculator suite',
            features: ['36 tools', 'Compliance', 'Tax optimization'],
            badge: 'PRO',
            badgeColor: 'bg-purple-500'
        }
    ];
    
    return (
        <>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 shadow-lg"
                 style={{ paddingTop: 'max(env(safe-area-inset-top), 24px)' }}>
                <h1 className="text-2xl font-bold mb-2">SuperCalc Pro</h1>
                <p className="text-blue-50 text-base font-semibold">Don't just project retirement. Stress-test it.</p>
                <p className="text-blue-100 text-sm mt-1">Choose a calculator to test income, super, pension rules, and real-life retirement risks.</p>
            </div>

            {/* Rates updated weekly + recent updates */}
            <div className="px-4 pt-3 pb-2">
                <UpdateMarker variant="compact" />
            </div>

            {subInfo.isPro && (
                <div className="px-4 pb-1 text-center">
                    <button
                        type="button"
                        onClick={() => setShowBilling(true)}
                        className="text-xs text-gray-500 hover:text-gray-800 underline decoration-gray-400/70 underline-offset-2 font-medium py-1"
                        title="Update card, invoices, or cancel at period end"
                    >
                        Manage billing
                    </button>
                </div>
            )}

            {/* Most popular: Start with Basic – clear primary CTA for mobile */}
            <div className="px-4 py-2">
                <button
                    type="button"
                    onClick={() => onSelectCalculator('basic')}
                    className="block w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-center font-bold py-4 px-4 rounded-2xl shadow-lg transition"
                >
                    Start with Basic – free
                </button>
            </div>

            {/* Retirement income needs planner */}
            <div className="px-4 py-2">
                <a
                    href="/calculators/retirement-income-needs-calculator.html"
                    className="block w-full bg-emerald-600 hover:bg-emerald-700 text-white text-center font-semibold py-3 px-4 rounded-2xl shadow-lg transition text-sm"
                >
                    Retirement income needs planner →
                </a>
            </div>
            
            {/* Calculator Cards */}
            <div className="p-4 space-y-4">
                {calculators.map(calc => (
                    <button
                        key={calc.id}
                        onClick={() => onSelectCalculator(calc.id)}
                        className={`w-full text-left bg-white rounded-2xl shadow-lg p-6 transition-all active:scale-98 ${
                            currentCalculator === calc.id ? 'ring-4 ring-blue-500' : ''
                        }`}
                        style={{ minHeight: '140px' }}
                    >
                        <div className="flex items-start gap-4">
                            <div className="text-5xl flex-shrink-0">{calc.icon}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                    <h2 className="text-lg font-bold text-gray-900">{calc.name}</h2>
                                    <span className={`${calc.badgeColor} text-white text-xs font-bold px-2 py-1 rounded`}>
                                        {calc.badge}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{calc.description}</p>
                                <div className="flex flex-wrap gap-2">
                                    {calc.features.map((feature, i) => (
                                        <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <svg className="w-6 h-6 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </button>
                ))}
            </div>

            <div className="px-4 pb-4">
                <UserTestimonialsSection />
            </div>

            {/* Blog link — inline, not fixed */}
            <div className="px-4 pt-2 pb-2">
                <a
                    href="/blog/"
                    style={{ display:'block', width:'100%', background:'#1e40af', color:'white', textAlign:'center', fontWeight:700, padding:'14px 16px', borderRadius:'16px', textDecoration:'none', fontSize:'16px' }}
                >
                    📖 Blog Index
                </a>
            </div>

            {/* Not sure which to use — inline, not fixed */}
            <div className="px-4 pt-1" style={{ paddingBottom:'calc(80px + env(safe-area-inset-bottom, 0px))' }}>
                <p className="text-center text-sm text-gray-600">
                    Not sure which to use?{' '}
                    <button
                        onClick={onShowGuide}
                        className="text-blue-600 font-semibold active:text-blue-800"
                    >
                        See Guide →
                    </button>
                </p>
            </div>
        </div>
        {showBilling && (
            <Suspense
                fallback={
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100]">
                        <div className="bg-white rounded-lg px-6 py-4 shadow-xl text-gray-600 text-sm">Loading…</div>
                    </div>
                }
            >
                <SubscriptionManagement onClose={() => setShowBilling(false)} />
            </Suspense>
        )}
        </>
    );
};

export default MobileCalculatorLanding;

